import { apiClient, createIdempotencyKey } from "@nimbus-erp/api-client";
import { PERMISSIONS, type WorkflowRequest } from "@nimbus-erp/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Descriptions,
  Drawer,
  Input,
  Space,
  Spin,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { WorkflowSnapshot } from "./WorkflowCommon";
import "../styles/erp-workflow.css";
export function WorkflowApprovalBridge() {
  const [request, setRequest] = useState<WorkflowRequest>();
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const busy = useRef(false),
    key = useRef("");
  const canRead = useAuthStore(
    (s) => s.user?.permissions.includes(PERMISSIONS.workflowRead) ?? false,
  );
  const navigate = useNavigate(),
    cache = useQueryClient();
  const { message } = App.useApp();
  useEffect(() => {
    apiClient.setApprovalRequiredHandler(
      canRead
        ? (r) => {
            key.current = createIdempotencyKey();
            setReason("");
            setRequest(r);
          }
        : undefined,
    );
    return () => apiClient.setApprovalRequiredHandler(undefined);
  }, [canRead]);
  const query = useQuery({
    queryKey: ["workflow", "preview", request],
    queryFn: () => apiClient.previewWorkflow(request!),
    enabled: !!request,
    retry: false,
  });
  const open = (id: string) => {
    setRequest(undefined);
    navigate(`/workflow?view=MINE&instanceId=${id}`);
  };
  const submit = async () => {
    if (busy.current || !request) return;
    if (!reason.trim()) {
      message.warning("请填写审批申请说明");
      return;
    }
    busy.current = true;
    setSaving(true);
    try {
      const result = await apiClient.submitWorkflow(
        request,
        reason,
        key.current,
      );
      void cache.invalidateQueries({ queryKey: ["workflow"] });
      message.success("申请已提交，审批完成后可在审批中心执行原业务");
      open(result.id);
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };
  return (
    <Drawer
      open={!!request}
      size={1080}
      title="业务审批申请"
      onClose={() => !saving && setRequest(undefined)}
      destroyOnHidden
      extra={
        <Space>
          {query.data?.current ? (
            <Button
              type="primary"
              onClick={() => open(query.data!.current!.id)}
            >
              查看已有申请
            </Button>
          ) : (
            <Button
              type="primary"
              disabled={!query.data?.required}
              loading={saving}
              onClick={() => void submit()}
            >
              提交审批申请
            </Button>
          )}
        </Space>
      }
    >
      {query.isPending ? (
        <Spin />
      ) : query.isError ? (
        <Alert type="error" title={(query.error as Error).message} />
      ) : (
        query.data && (
          <div className="workflow-detail">
            <Alert
              type="info"
              title={
                query.data.required
                  ? "业务尚未执行。请核对下面保存的内容；审批通过后可直接执行，内容或来源变化需要重新审批。"
                  : "当前业务未匹配到需要审批的有效模板，可关闭此窗口并按原业务权限办理。"
              }
            />
            <Descriptions
              bordered
              column={2}
              items={[
                {
                  key: "source",
                  label: "业务来源",
                  children: query.data.sourceTitle,
                },
                {
                  key: "template",
                  label: "匹配模板",
                  children: query.data.templateName ?? "未配置",
                },
                {
                  key: "amount",
                  label: "审批金额",
                  children: query.data.amount?.toFixed(2) ?? "无成本查看权限",
                },
                {
                  key: "basis",
                  label: "金额口径",
                  children: query.data.amountBasis,
                },
              ]}
            />
            {query.data.current ? (
              <Alert
                type="success"
                title="相同内容已有审批申请，已保留原申请记录。"
              />
            ) : (
              <>
                <label htmlFor="workflow-submit-reason">审批申请说明</label>
                <Input.TextArea
                  id="workflow-submit-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={300}
                  rows={3}
                />
              </>
            )}
            <WorkflowSnapshot data={query.data.snapshot} />
          </div>
        )
      )}
    </Drawer>
  );
}
